import { Divider, Stack } from '@/components/layout';
import { Button, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import MutatePlayerView from '@/features/player/components/mutate-player';
import { createStyles, useScaling } from '@/hooks';
import type { AuthStackScreenProps } from '@/navigations/types';
import { useSessionStore } from '@/stores/session-store';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function LoginCredentialsScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useNavigation<AuthStackScreenProps<'LoginCredentials'>['navigation']>();
  const user = useSessionStore(state => state.user);

  const emailDisplay = useMemo(() => {
    const email = user?.email?.trim();
    return email && email.length > 0 ? email : STRINGS.auth.loginCredentials.emailUnavailable;
  }, [user?.email]);

  const handleUpdatePassword = () => {
    navigation.navigate('ChangePassword');
  };

  const fields = (
    <>
      <Stack gap={scale(2)}>
        <Text
          fontWeight="400"
          fontSize={scale({ phone: 9, tablet: 11 })}
          fontFamily={'Inter'}
          style={styles.fieldLabel}
          transform="uppercase">
          {STRINGS.auth.loginCredentials.labelEmail}
        </Text>
        <Text
          fontWeight="600"
          fontSize={scale({ phone: 11, tablet: 14 })}
          fontFamily={'Inter'}
          style={styles.fieldValue}>
          {emailDisplay}
        </Text>
      </Stack>

      <Stack gap={scale(2)}>
        <Text
          fontWeight="400"
          fontSize={scale({ phone: 9, tablet: 11 })}
          fontFamily={'Inter'}
          style={styles.fieldLabel}
          transform="uppercase">
          {STRINGS.auth.loginCredentials.labelPassword}
        </Text>
        <Text
          fontWeight="600"
          fontSize={scale({ phone: 11, tablet: 14 })}
          fontFamily={'Inter'}
          style={styles.fieldValue}>
          {STRINGS.auth.loginCredentials.maskedPassword}
        </Text>
      </Stack>
    </>
  );

  return (
    <Stack flex={1} backgroundColor={isTablet ? colors['1'] : colors['0']}>
      <MutatePlayerView.Header title={STRINGS.auth.loginCredentials.title} isBoldTitle />
      <Divider color={colors['5']} spacing={scale(0)} thickness={StyleSheet.hairlineWidth} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          isTablet && styles.scrollContentTablet,
          { paddingBottom: insets.bottom + scale(24) },
        ]}
        showsVerticalScrollIndicator={false}>
        {isTablet ? (
          <Stack style={styles.tabletCard} gap={scale(16)} width="100%" maxWidth={scale(375)}>
            {fields}
            <Button onPress={handleUpdatePassword} style={styles.primaryButton}>
              <Button.Text variant="Font Inter/Subtitle/Medium 12px" transform="uppercase">
                {STRINGS.auth.loginCredentials.buttonUpdatePassword}
              </Button.Text>
            </Button>
          </Stack>
        ) : (
          <Stack gap={scale(16)} width="100%">
            {fields}
            <Button onPress={handleUpdatePassword} style={styles.primaryButton}>
              <Button.Text variant="Font Inter/Subtitle/Regular 11px" transform="uppercase">
                {STRINGS.auth.loginCredentials.buttonUpdatePassword}
              </Button.Text>
            </Button>
          </Stack>
        )}
      </ScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  scroll: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : colors['0'],
  },
  scrollContent: {
    paddingHorizontal: scale(18),
    paddingTop: scale(16),
    flexGrow: 1,
  },
  scrollContentTablet: {
    paddingHorizontal: scale(24),
    paddingTop: scale(24),
    alignItems: 'center',
  },
  tabletCard: {
    backgroundColor: colors['0'],
    padding: scale(24),
  },
  fieldLabel: {
    color: colors['6'],
  },
  fieldValue: {
    color: colors.Blue,
  },
  primaryButton: {
    alignSelf: 'stretch',
    minHeight: scale(30),
    height: scale(30),
    paddingVertical: scale(0),
    ...(isTablet
      ? {
          minHeight: scale(40),
          height: scale(40),
        }
      : {}),
  },
}));
